import { Injectable } from '@angular/core';
import { ModalService, MsiModalConfig, MsiModalRef } from '@msi/cobalt';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BasicModalComponent } from 'src/app/components/node-workspace/basic-modal/basic-modal.component';
import { ModalData, ModalInput, ModalResponse } from 'src/app/interfaces/modal.interface';

@Injectable({
	providedIn: 'root',
})
export class BasicModalService {
	private modalCount = 0;
	public get isModalDisplayed() {
		return this.modalCount > 0;
	}

	constructor(private modalService: ModalService) {}

	/**
	 * Creates a basic-modal and returns a filtered observable that
	 * emits user input only if the action was not canceled
	 */
	public createModal(config: ModalData, modalInput?: ModalInput): Observable<ModalResponse> {
		return this.createModalUnfiltered(config, modalInput)?.pipe(
			filter(userResponse => !!userResponse && !userResponse.canceled)
		);
	}

	/**
	 * Creates a basic-modal and returns an unfiltered observable that emits the user input
	 */
	public createModalUnfiltered(config: ModalData, modalInput?: ModalInput): Observable<ModalResponse> {
		return this.createModalRef(config, modalInput)?.afterClosed();
	}

	/**
	 * Creates a basic-modal and returns a reference to it
	 * This is needed for the modal to be closed programmatically
	 */
	public createModalRef(config: ModalData, modalInput?: ModalInput): MsiModalRef {
		const modalConfig = this.getModalConfig(config, modalInput);
		if (!modalConfig) {
			return;
		}
		const modalRef = this.modalService.open(BasicModalComponent, modalConfig);
		modalRef.afterClosed()?.subscribe(() => this.modalCount--);
		this.modalCount++;
		return modalRef;
	}

	/**
	 * Creates a ModalConfig object by combining the selected configuration's
	 * parameters with any additional user-input parameters
	 */
	private getModalConfig(config: ModalData, modalInput?: ModalInput): MsiModalConfig {
		if (!config) {
			return;
		}
		const modalConfig: MsiModalConfig = {
			disableClose: !!config.disableClose,
			hasBackdrop: true,
			data: {
				...config,
				...modalInput,
			},
		};
		return modalConfig;
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
